/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Grid,
    Alert,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    IconButton,
    Button,
} from '@mui/material';
import {
    Person,
    ExpandMore,
    Flight,
    Hotel,
    CheckCircle,
    Add,
    Delete,
    Info,
} from '@mui/icons-material';

export interface TravelerInfo {
    id: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    dateOfBirth: string;
    gender: 'male' | 'female' | 'other' | '';
    email: string;
    phone: string;
    passportNumber?: string;
    passportExpiry?: string;
    passportCountry?: string;
    knownTravelerNumber?: string; // TSA PreCheck
    redressNumber?: string;
    seatPreference?: 'window' | 'aisle' | 'middle' | '';
    mealPreference?: 'standard' | 'vegetarian' | 'vegan' | 'kosher' | 'halal' | 'gluten-free' | '';
    specialRequests?: string;
    isPrimary: boolean;
}

interface UserProfile {
    userId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    dateOfBirth?: string;
    gender?: string;
    passportNumber?: string;
    passportExpiry?: string;
    passportCountry?: string;
    knownTravelerNumber?: string;
}

interface TravelerDetailsStepProps {
    travelers: TravelerInfo[];
    onTravelersChange: (travelers: TravelerInfo[]) => void;
    userProfile?: UserProfile | null;
    hasFlights: boolean;
    hasInternationalFlights: boolean;
    hasHotels: boolean;
    passengerCount?: number;
}

const emptyTraveler = (isPrimary: boolean = false): TravelerInfo => ({
    id: `traveler-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    firstName: '',
    lastName: '',
    middleName: '',
    dateOfBirth: '',
    gender: '',
    email: '',
    phone: '',
    passportNumber: '',
    passportExpiry: '',
    passportCountry: '',
    knownTravelerNumber: '',
    redressNumber: '',
    seatPreference: '',
    mealPreference: '',
    specialRequests: '',
    isPrimary,
});

// Helper function to safely parse and format date
const formatDateForInput = (dateValue: string | undefined | null): string => {
    if (!dateValue) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
    }
    
    try {
        const date = new Date(dateValue);
        // Check if date is valid and not in the far future (invalid parsing)
        if (isNaN(date.getTime()) || date.getFullYear() > 2100 || date.getFullYear() < 1900) {
            return '';
        }
        return date.toISOString().split('T')[0];
    } catch {
        return '';
    }
};

const TravelerDetailsStep: React.FC<TravelerDetailsStepProps> = ({
    travelers,
    onTravelersChange,
    userProfile,
    hasFlights,
    hasInternationalFlights,
    hasHotels,
    passengerCount = 1,
}) => {
    const [expandedTraveler, setExpandedTraveler] = useState<string | false>('traveler-0');
    const [profileApplied, setProfileApplied] = useState(false);

    // Initialize travelers based on passengerCount
    useEffect(() => {
        if (travelers.length === 0 && passengerCount > 0) {
            const initialTravelers: TravelerInfo[] = [];
            for (let i = 0; i < passengerCount; i++) {
                initialTravelers.push(emptyTraveler(i === 0));
            }
            onTravelersChange(initialTravelers);
            console.log(`Initialized ${passengerCount} travelers`);
        } else if (travelers.length < passengerCount) {
            // Add more travelers if needed
            const additionalTravelers: TravelerInfo[] = [];
            for (let i = travelers.length; i < passengerCount; i++) {
                additionalTravelers.push(emptyTraveler(false));
            }
            onTravelersChange([...travelers, ...additionalTravelers]);
            console.log(`Added ${additionalTravelers.length} more travelers to reach ${passengerCount}`);
        }
    }, [passengerCount, travelers.length, onTravelersChange, travelers]);

    // Pre-fill from user profile for primary traveler
    useEffect(() => {
        if (userProfile && travelers.length > 0 && !profileApplied) {
            const primaryTraveler = travelers.find(t => t.isPrimary);
            if (primaryTraveler && !primaryTraveler.firstName && !primaryTraveler.lastName) {
                const updatedTravelers: TravelerInfo[] = travelers.map(t => {
                    if (t.isPrimary) {
                        const gender: TravelerInfo['gender'] = 
                            userProfile.gender === 'male' || userProfile.gender === 'female' || userProfile.gender === 'other' 
                                ? userProfile.gender 
                                : '';
                        
                        // Safely parse the date of birth
                        const dob = formatDateForInput(userProfile.dateOfBirth);
                        
                        return {
                            ...t,
                            firstName: userProfile.firstName || '',
                            lastName: userProfile.lastName || '',
                            email: userProfile.email || '',
                            phone: userProfile.phone || '',
                            dateOfBirth: dob,
                            gender,
                            passportNumber: userProfile.passportNumber || '',
                            passportExpiry: formatDateForInput(userProfile.passportExpiry),
                            passportCountry: userProfile.passportCountry || '',
                            knownTravelerNumber: userProfile.knownTravelerNumber || '',
                        };
                    }
                    return t;
                });
                onTravelersChange(updatedTravelers);
                setProfileApplied(true);
            }
        }
    }, [userProfile, travelers, profileApplied, onTravelersChange]);

    const updateTraveler = (id: string, field: keyof TravelerInfo, value: string) => {
        const updatedTravelers = travelers.map(t => {
            if (t.id === id) {
                return { ...t, [field]: value };
            }
            return t;
        });
        onTravelersChange(updatedTravelers);
    };

    const addTraveler = () => {
        const newTraveler = emptyTraveler(false);
        onTravelersChange([...travelers, newTraveler]);
        // Expand the new traveler
        setExpandedTraveler(`traveler-${travelers.length}`);
    };

    const removeTraveler = (id: string) => {
        if (travelers.length > 1) {
            onTravelersChange(travelers.filter(t => t.id !== id));
        }
    };

    const isTravelerComplete = (traveler: TravelerInfo): boolean => {
        // Base required fields for all travelers
        const requiredFields = ['firstName', 'lastName'];
        
        // Email and phone only required for primary traveler
        if (traveler.isPrimary) {
            requiredFields.push('email', 'phone');
        }
        
        if (hasFlights) {
            requiredFields.push('dateOfBirth', 'gender');
        }
        if (hasInternationalFlights) {
            requiredFields.push('passportNumber', 'passportExpiry', 'passportCountry');
        }
        return requiredFields.every(field => !!traveler[field as keyof TravelerInfo]);
    };

    const getTravelerStatus = (traveler: TravelerInfo): 'complete' | 'incomplete' => {
        return isTravelerComplete(traveler) ? 'complete' : 'incomplete';
    };

    // Calculate how many more travelers are needed
    const travelersNeeded = Math.max(0, passengerCount - travelers.length);

    return (
        <Box>
            {/* Pre-fill notice */}
            {userProfile && profileApplied && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} icon={<CheckCircle />}>
                    <Typography variant="body2">
                        We've pre-filled your information from your profile. Please review and complete any missing details.
                    </Typography>
                </Alert>
            )}

            {/* Required info notice */}
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<Info />}>
                <Typography variant="body2">
                    <strong>Required for booking:</strong> Full legal name (as on ID/passport)
                    {hasFlights && ', date of birth, gender'}
                    {hasInternationalFlights && ', passport details'}.
                    Contact info required for primary traveler only.
                </Typography>
            </Alert>

            {/* Warning if not enough travelers */}
            {travelersNeeded > 0 && (
                <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2">
                        <strong>Missing travelers:</strong> Please add details for {travelersNeeded} more traveler{travelersNeeded > 1 ? 's' : ''} 
                        ({travelers.length} of {passengerCount} added)
                    </Typography>
                </Alert>
            )}

            {/* Traveler forms */}
            {travelers.map((traveler, index) => (
                <Accordion
                    key={traveler.id}
                    expanded={expandedTraveler === `traveler-${index}`}
                    onChange={(_, isExpanded) => setExpandedTraveler(isExpanded ? `traveler-${index}` : false)}
                    sx={{ mb: 2, borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{ borderRadius: 2, '&.Mui-expanded': { minHeight: 56 } }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Person sx={{ color: traveler.isPrimary ? '#FF6B35' : '#666' }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {traveler.firstName && traveler.lastName
                                        ? `${traveler.firstName} ${traveler.lastName}`
                                        : `Traveler ${index + 1}`}
                                    {traveler.isPrimary && (
                                        <Chip label="Primary" size="small" sx={{ ml: 1, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600, height: 20 }} />
                                    )}
                                </Typography>
                                {traveler.email && (
                                    <Typography variant="caption" color="text.secondary">{traveler.email}</Typography>
                                )}
                            </Box>
                            <Chip
                                label={getTravelerStatus(traveler)}
                                size="small"
                                sx={{
                                    bgcolor: getTravelerStatus(traveler) === 'complete' ? '#E8F5E9' : '#FFF3E0',
                                    color: getTravelerStatus(traveler) === 'complete' ? '#2E7D32' : '#E65100',
                                    fontWeight: 600,
                                    textTransform: 'capitalize',
                                }}
                            />
                            {!traveler.isPrimary && travelers.length > passengerCount && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); removeTraveler(traveler.id); }}
                                    sx={{ ml: 1 }}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 2 }}>
                        {/* Personal Information */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
                            Personal Information
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="First Name *"
                                    value={traveler.firstName}
                                    onChange={(e) => updateTraveler(traveler.id, 'firstName', e.target.value)}
                                    placeholder="As on ID/passport"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Middle Name"
                                    value={traveler.middleName}
                                    onChange={(e) => updateTraveler(traveler.id, 'middleName', e.target.value)}
                                    placeholder="Optional"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Last Name *"
                                    value={traveler.lastName}
                                    onChange={(e) => updateTraveler(traveler.id, 'lastName', e.target.value)}
                                    placeholder="As on ID/passport"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            {hasFlights && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Date of Birth *"
                                            type="date"
                                            value={traveler.dateOfBirth || ''}
                                            onChange={(e) => updateTraveler(traveler.id, 'dateOfBirth', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                max: new Date().toISOString().split('T')[0], // Can't be born in the future
                                                min: '1900-01-01', // Reasonable minimum
                                            }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Gender *</InputLabel>
                                            <Select
                                                value={traveler.gender}
                                                label="Gender *"
                                                onChange={(e) => updateTraveler(traveler.id, 'gender', e.target.value)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                <MenuItem value="male">Male</MenuItem>
                                                <MenuItem value="female">Female</MenuItem>
                                                <MenuItem value="other">Other / Prefer not to say</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                        </Grid>

                        {/* Contact Information - Only required for primary traveler */}
                        {traveler.isPrimary && (
                            <>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
                                    Contact Information
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email Address *"
                                            type="email"
                                            value={traveler.email}
                                            onChange={(e) => updateTraveler(traveler.id, 'email', e.target.value)}
                                            placeholder="For confirmation emails"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone Number *"
                                            type="tel"
                                            value={traveler.phone}
                                            onChange={(e) => updateTraveler(traveler.id, 'phone', e.target.value)}
                                            placeholder="+1 (555) 123-4567"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {/* Passport Information (for international flights) */}
                        {hasInternationalFlights && (
                            <>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Flight sx={{ fontSize: 18 }} /> Passport Information
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Passport Number *"
                                            value={traveler.passportNumber}
                                            onChange={(e) => updateTraveler(traveler.id, 'passportNumber', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Passport Expiry *"
                                            type="date"
                                            value={traveler.passportExpiry || ''}
                                            onChange={(e) => updateTraveler(traveler.id, 'passportExpiry', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                min: new Date().toISOString().split('T')[0], // Must be in the future
                                            }}
                                            helperText="Must be valid for 6+ months"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Issuing Country *"
                                            value={traveler.passportCountry}
                                            onChange={(e) => updateTraveler(traveler.id, 'passportCountry', e.target.value)}
                                            placeholder="e.g., United States"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {/* Flight Preferences */}
                        {hasFlights && (
                            <>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
                                    Flight Preferences (Optional)
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="TSA PreCheck / Known Traveler #"
                                            value={traveler.knownTravelerNumber}
                                            onChange={(e) => updateTraveler(traveler.id, 'knownTravelerNumber', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Seat Preference</InputLabel>
                                            <Select
                                                value={traveler.seatPreference}
                                                label="Seat Preference"
                                                onChange={(e) => updateTraveler(traveler.id, 'seatPreference', e.target.value)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                <MenuItem value="">No preference</MenuItem>
                                                <MenuItem value="window">Window</MenuItem>
                                                <MenuItem value="aisle">Aisle</MenuItem>
                                                <MenuItem value="middle">Middle</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Meal Preference</InputLabel>
                                            <Select
                                                value={traveler.mealPreference}
                                                label="Meal Preference"
                                                onChange={(e) => updateTraveler(traveler.id, 'mealPreference', e.target.value)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                <MenuItem value="">Standard</MenuItem>
                                                <MenuItem value="vegetarian">Vegetarian</MenuItem>
                                                <MenuItem value="vegan">Vegan</MenuItem>
                                                <MenuItem value="kosher">Kosher</MenuItem>
                                                <MenuItem value="halal">Halal</MenuItem>
                                                <MenuItem value="gluten-free">Gluten-free</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {/* Special Requests */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
                            Special Requests (Optional)
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Special Requests"
                            value={traveler.specialRequests}
                            onChange={(e) => updateTraveler(traveler.id, 'specialRequests', e.target.value)}
                            placeholder="Wheelchair assistance, early check-in, dietary needs, etc."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </AccordionDetails>
                </Accordion>
            ))}

            {/* Add Traveler Button - only show if below passenger count */}
            {travelersNeeded > 0 && (
                <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addTraveler}
                    sx={{
                        borderRadius: '20px',
                        textTransform: 'none',
                        borderColor: '#FF6B35',
                        color: '#FF6B35',
                        fontWeight: 700,
                        '&:hover': { 
                            borderColor: '#E55A2B', 
                            bgcolor: '#FFF3E0' 
                        },
                    }}
                >
                    Add Traveler {travelers.length + 1} of {passengerCount} (Required)
                </Button>
            )}
        </Box>
    );
};

export default TravelerDetailsStep;
